import { Box, Stack } from '@mui/material';
import {
  CONDUCT_ENTRIES_GROUP_BY_FILTER_KEYS,
  CONDUCT_ENTRIES_QUERY_FILTER_KEYS,
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetConductEntriesQueryFilters,
  SORT_DIRECTION,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { ConductChartIconButton, ConductChart } from '@schooly/components/charts';
import {
  PageHeader,
  PageHeaderSearchInput,
  StoredFilterSections,
  useFiltersStateFromSearchParams,
  useGroupByFromSearchParams,
  useLastAppliedFiltersState,
  useSaveLastAppliedFiltersState,
  useSyncFiltersStateWithSearchParams,
} from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { SchoolPropertyType } from '@schooly/constants';
import { useFlag } from '@schooly/hooks/use-flag';
import { AccessDenied, ChartIcon, PlusIcon } from '@schooly/style';
import { endOfToday, format, startOfToday } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { NoListItemsStub } from '../../components/common/NoListItemsStub/NoListItemsStub';
import useAppLocation from '../../hooks/useAppLocation';
import { useSchool } from '../../hooks/useSchool';
import useSchoolYears from '../../hooks/useSchoolYears';
import useUserCounts from '../../hooks/useUserCounts';
import { PAGE_SIZE } from './ConductGrid';
import { ConductExport } from './ConductExport';
import { ConductEntriesFilters } from './ConductFilters';
import { ConductFlatGrid } from './ConductFlatGrid';
import { ConductGroupedGrid } from './ConductGroupedGrid';
import { ConductNoStudentsStub } from './ConductNoStudentsStub';
import { useConductData } from './useConductData';

type ConductContentProps = {
  initialFilters?: GetConductEntriesQueryFilters;
  initialGroupBy?: FilterKeys.Student | null;
};

export const ConductContent: FC<ConductContentProps> = ({ initialFilters, initialGroupBy }) => {
  const { showNotification } = useNotifications();
  const { formatMessage } = useIntl();
  const { userCounts } = useUserCounts();
  const { schoolId = '' } = useSchool();
  const navigate = useNavigate();
  const location = useAppLocation();

  const { lastAppliedFilter, lastAppliedGroupBy } = useLastAppliedFiltersState({
    type: StoredFilterSections.Conduct,
    filterKeys: CONDUCT_ENTRIES_QUERY_FILTER_KEYS,
    groupByKeys: CONDUCT_ENTRIES_GROUP_BY_FILTER_KEYS,
    schoolId: schoolId || '',
  });

  const groupByFromSearchParams = useGroupByFromSearchParams([FilterKeys.Student]);
  const groupByFromFiltersOrProps = useMemo(() => {
    if (lastAppliedGroupBy !== undefined) return lastAppliedGroupBy;
    if (groupByFromSearchParams !== undefined) return groupByFromSearchParams;
    return initialGroupBy;
  }, [groupByFromSearchParams, initialGroupBy, lastAppliedGroupBy]);

  const [groupBy, setGroupBy] = useState<FilterKeys.Student | null>(
    groupByFromFiltersOrProps === undefined ? FilterKeys.Student : groupByFromFiltersOrProps,
  );

  const { permissions } = useAuth();
  const [isChartsOpened, openCharts, closeCharts] = useFlag(false);

  const defaultFilters: GetConductEntriesQueryFilters = useMemo(
    () => ({
      [FilterKeys.Date]: [
        format(startOfToday(), DEFAULT_DATE_FORMAT_FNS),
        format(endOfToday(), DEFAULT_DATE_FORMAT_FNS),
      ],
    }),
    [],
  );

  const initialFiltersState = useFiltersStateFromSearchParams({
    filterKeys: CONDUCT_ENTRIES_QUERY_FILTER_KEYS,
    defaultFilters,
    initialFilters,
  });

  const defaultUserFilters = useMemo(
    () => ({ ...defaultFilters, ...initialFilters }),
    [defaultFilters, initialFilters],
  );

  const isGrouped = groupBy === FilterKeys.Student;

  const {
    params,
    flatQueryResult,
    groupedQueryResult,
    conductLogsCount,
    error,
    setFilters,
    setSort,
    setQuery,
    resetSortForGrouped,
  } = useConductData({
    initialParams: {
      schoolId,
      pageSize: PAGE_SIZE,
      filters: lastAppliedFilter ?? initialFiltersState,
      sort: { columnTextId: 'student', direction: SORT_DIRECTION.ASC },
      query: '',
    },
    isGrouped,
  });

  useEffect(() => {
    if (isGrouped) {
      resetSortForGrouped();
    }
  }, [isGrouped, resetSortForGrouped]);

  const filters = params.filters;
  const sort = params.sort;
  const query = params.query ?? '';

  useSyncFiltersStateWithSearchParams({
    pathname: '/conduct',
    filters,
    groupBy,
  });

  useSaveLastAppliedFiltersState({
    type: StoredFilterSections.Conduct,
    filters,
    groupBy: groupBy ?? null,
    schoolId: schoolId || '',
  });

  const { defaultValidity } = useSchoolYears();

  const canEdit = permissions.includes('conduct_manager');
  const canView = permissions.includes('conduct_viewer');

  const exportParams = useMemo(
    () => ({ schoolId, filters, sort, query }),
    [schoolId, filters, sort, query],
  );

  const handleExportSuccess = useCallback(() => {
    showNotification({
      message: formatMessage({ id: 'conduct-ExportSuccess' }, { count: conductLogsCount }),
      type: 'info',
    });
  }, [showNotification, conductLogsCount, formatMessage]);

  const handleAddButtonClick = useCallback(() => {
    navigate('/conduct/new', {
      state: { backgroundLocation: location },
    });
  }, [location, navigate]);

  const noResults = conductLogsCount === 0;

  const chartButton = !isChartsOpened ? (
    <ConductChartIconButton
      schoolId={schoolId}
      conductTypeOptionIds={filters.conduct_type}
      onClick={openCharts}
      disabled={!userCounts?.student || noResults}
      inverse
    >
      <ChartIcon />
    </ConductChartIconButton>
  ) : undefined;

  if (!canView || error) {
    return <AccessDenied />;
  }

  return (
    <>
      <Stack gap={1}>
        <PageHeader
          buttonTextId="conduct-NewLog"
          pageTitleTextId="conduct-Title"
          showActionButton={permissions.includes('conduct_manager')}
          buttonIcon={<PlusIcon />}
          buttonDisabled={!userCounts?.student}
          onButtonClick={handleAddButtonClick}
          actionsContent={
            !!conductLogsCount && (
              <Box sx={{ mr: 2.5 }}>
                <ConductExport
                  params={exportParams}
                  onSuccess={handleExportSuccess}
                  conductLogsCount={conductLogsCount}
                />
              </Box>
            )
          }
        >
          <Stack gap={2} direction="row" alignItems="center" width="100%">
            <PageHeaderSearchInput
              value={query}
              onChangeText={setQuery}
              placeholder={formatMessage({ id: 'people-Search' })}
            />
          </Stack>
        </PageHeader>

        <ConductEntriesFilters
          groupBy={groupBy}
          onSetGroupBy={setGroupBy}
          defaultFilters={defaultFilters}
          onSetFilters={setFilters}
          filters={filters}
          schoolId={schoolId || ''}
          defaultSchoolYear={defaultValidity}
          defaultUserFilters={defaultUserFilters}
          defaultUserGroupBy={initialGroupBy === undefined ? FilterKeys.Student : initialGroupBy}
        />
      </Stack>

      {isChartsOpened && !noResults && (
        <Stack mt={3}>
          <ConductChart
            schoolId={schoolId}
            schoolPropertyType={SchoolPropertyType.House}
            conductTypeOptionIds={filters.conduct_type}
            onCloseChart={closeCharts}
            filters={filters}
          />
        </Stack>
      )}

      {isGrouped ? (
        <ConductGroupedGrid
          schoolId={schoolId}
          filters={filters}
          canEdit={canEdit}
          onChangeSort={setSort}
          headerRightIcon={chartButton}
          sort={sort}
          queryResult={groupedQueryResult}
        />
      ) : (
        <ConductFlatGrid
          schoolId={schoolId}
          sort={sort}
          canEdit={canEdit}
          onChangeSort={setSort}
          headerRightIcon={chartButton}
          queryResult={flatQueryResult}
        />
      )}

      {!userCounts?.student ? (
        <ConductNoStudentsStub />
      ) : (
        conductLogsCount === 0 && (
          <NoListItemsStub
            titleText={<FormattedMessage id="conduct-NoConductExist-title" />}
            subTitleText={
              canEdit ? <FormattedMessage id="conduct-NoConductExist-subtitle" /> : undefined
            }
            buttonText={canEdit ? <FormattedMessage id="conduct-NewLog" /> : undefined}
            onButtonClick={handleAddButtonClick}
            type="conduct"
          />
        )
      )}
    </>
  );
};
